import { Fragment, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// Fix Datum-Shift: pg liefert DATE-Spalten als ISO-Timestamp der LOKALEN
// Mitternacht in UTC (bei UTC+2 also '…-14T22:00:00.000Z' fuer den 15.).
// Reines slice(0,10) wuerde den UTC-Shift uebernehmen (-1 Tag) - deshalb
// als lokales Datum interpretieren und daraus YYYY-MM-DD bauen.
const toLocalDateStr = (v) => {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s.slice(0, 10);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const EMPTY = () => ({
  baustelle_id: '', datum: new Date().toISOString().split('T')[0],
  bauleiter: '', temperatur: '', wetter: '', wind: '',
  niederschlag: '', sichtweite: '', wetter_bem: '',
  arbeitsbeginn: '', arbeitsende: '', std_gesamt: '',
  anzahl_monteure: '', gesamtstunden: '',
  leistungen: '', stillstand: false, stillstand_grund: '',
  foto_doku: false, anfahrt: false, status: 'entwurf',
  sig_ersteller: '', sig_bauleiter: '', material: [], stunden: [],
});

// A.5.2 Stunden-Konstanten — muessen mit dem Backend (A.2/A.5.1) uebereinstimmen.
const STUNDEN_TYPEN = ['regulaer', 'regie', 'zusatz'];
const TYP_LABELS = { regulaer: 'Regulär', regie: 'Regie', zusatz: 'Zusatz' };
const typAbrechenbarDefault = (typ) => typ !== 'regulaer'; // regie + zusatz = true
const positionFieldVisible = (row) => row.typ !== 'regulaer' || !!row.abrechenbar;
const isMigrationsZeile = (row) => typeof row.bemerkung === 'string'
  && row.bemerkung.startsWith('Migriert aus alter pauschaler');

// Counter fuer stabile React-keys an noch-nicht-gespeicherten Zeilen
// (geladene Zeilen bekommen "db-{id}", neue Zeilen "new-N").
let _stundeRowCounter = 0;
const newStundeKey = () => `new-${++_stundeRowCounter}`;
const newStundeRow = () => ({
  _key: newStundeKey(),
  user_id: null,
  subunternehmer_id: null,
  subunternehmer_firma: null,
  rolle_id: null,
  stunden: '',
  typ: 'regulaer',
  abrechenbar: typAbrechenbarDefault('regulaer'),
  projekt_position_id: null,
  bemerkung: '',
});

// Live-Aggregat im Frontend - gleiche Formel wie A.3-Backend, aber
// beschraenkt auf Zeilen mit gueltiger Stundenanzahl > 0. Leere/neue
// Zeilen werden nicht als Person gezaehlt, weil sie ohnehin nicht
// gespeichert werden (Backend-Pre-Pass wuerde sie zurueckweisen).
const calcAggregat = (rows) => {
  let summe = 0;
  const distinctUsers = new Set();
  let nullCount = 0;
  for (const r of rows) {
    const n = Number(r.stunden);
    if (!Number.isFinite(n) || n <= 0) continue;
    summe += n;
    if (r.user_id == null) nullCount += 1;
    else distinctUsers.add(r.user_id);
  }
  const personen = distinctUsers.size + nullCount;
  return {
    summe,
    personen,
    durchschnitt: personen > 0 ? summe / personen : null,
  };
};

function SignaturePad({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  useEffect(() => {
    if (value && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, 400, 120);
        ctx.drawImage(img, 0, 0);
        setHasSig(true);
      };
      img.src = value;
    }
  }, [value]);

  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const s = e.touches ? e.touches[0] : e;
    return { x: (s.clientX - r.left) * (400 / r.width), y: (s.clientY - r.top) * (120 / r.height) };
  };

  const start = e => { e.preventDefault(); drawing.current = true; const p = pos(e, canvasRef.current); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = e => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#0d0d0d'; const p = pos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const stop = e => { if (!drawing.current) return; drawing.current = false; setHasSig(true); onChange(canvasRef.current.toDataURL()); };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, 400, 120); setHasSig(false); onChange(''); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="form-label">{label}</label>
        {hasSig && <button type="button" onClick={clear} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}>Löschen</button>}
      </div>
      <canvas ref={canvasRef} width={400} height={120}
        style={{ width: '100%', height: 120, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'white', cursor: 'crosshair', touchAction: 'none' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
      {!hasSig && <div style={{ marginTop: -60, textAlign: 'center', color: 'var(--text-3)', fontSize: 12, pointerEvents: 'none', paddingBottom: 40 }}>Hier unterschreiben</div>}
    </div>
  );
}

export default function BautagebuchForm() {
  const { id: rawId } = useParams();
  const id = (rawId && rawId !== 'erstellen') ? rawId : null;
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY());
  const [baustellen, setBaustellen] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [projektPositionen, setProjektPositionen] = useState([]);
  // M2.2: Rezepte (Projektposition -> Lager-Material + Faktor) der gewaehlten
  // Baustelle fuer die Live-Vorschau der automatischen Abzuege.
  const [rezepte, setRezepte] = useState([]);
  // A.5.2 neu: Mitarbeiter + Funktions-Rollen fuer die Stundenzeilen
  const [users, setUsers] = useState([]);
  const [rollen, setRollen] = useState([]);
  // A.7: Subunternehmer-Stammdaten fuer externe Stundenzeilen
  const [subunternehmer, setSubunternehmer] = useState([]);
  const [saving, setSaving] = useState(false);
  const [wetterLoading, setWetterLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/baustellen').then(r => setBaustellen(r.data)).catch(() => {});
    api.get('/material').then(r => setKatalog(r.data)).catch(() => {});
    // /api/mitarbeiter-optionen ist NICHT adminOnly und liefert nur die
    // fuer die Stundenzeile noetigen Felder (id/name/aktiv/rolle_*). So
    // koennen auch Monteure beim Erfassen Mitarbeiter auswaehlen, ohne
    // dass das Frontend Admin-/Stammdaten-Felder bekommt.
    api.get('/mitarbeiter-optionen').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/rollen').then(r => setRollen(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // A.7: /subunternehmer-optionen ist NICHT adminOnly (nur aktive, {id, firma})
    // - Monteure koennen so bei externen Zeilen aus den Stammdaten waehlen.
    api.get('/subunternehmer-optionen').then(r => setSubunternehmer(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Projektpositionen + Rezepte passend zur gewählten Baustelle laden.
  useEffect(() => {
    if (!form.baustelle_id) { setProjektPositionen([]); setRezepte([]); return; }
    api.get(`/projekt-positionen?baustelle_id=${form.baustelle_id}`)
      .then(r => setProjektPositionen(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjektPositionen([]));
    api.get(`/projekt-positionen/rezepte?baustelle_id=${form.baustelle_id}`)
      .then(r => setRezepte(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRezepte([]));
  }, [form.baustelle_id]);

  useEffect(() => {
    if (isEdit && id) {
      // A.5.2: BT-Header + Stunden separat laden (Stunden-Endpoint
      // existiert seit A.2). Beide parallel.
      Promise.all([
        api.get(`/bautagebuecher/${id}`),
        api.get(`/bautagebuecher/${id}/stunden`),
      ]).then(([rBT, rSt]) => {
        const d = rBT.data;
        const stundenRows = (Array.isArray(rSt.data) ? rSt.data : []).map(s => ({
          _key: `db-${s.id}`,
          user_id: s.user_id,
          // A.7: Sub-Zuordnung + Firma (Firma nur fuer den Bestandsschutz im
          // Dropdown, falls der Sub inzwischen archiviert ist).
          subunternehmer_id: s.subunternehmer_id ?? null,
          subunternehmer_firma: s.subunternehmer_firma || null,
          rolle_id: s.rolle_id,
          stunden: s.stunden != null ? String(s.stunden) : '',
          typ: s.typ || 'regulaer',
          abrechenbar: !!s.abrechenbar,
          projekt_position_id: s.projekt_position_id,
          bemerkung: s.bemerkung || '',
        }));
        setForm({
          ...EMPTY(), ...d,
          // Fix Datum-Shift: ISO-Timestamp -> lokales YYYY-MM-DD (Helper oben).
          // Vorher zeigte das date-Input leer und der rohe ISO-String
          // verschob das Datum bei jedem Save um -1 Tag.
          datum: toLocalDateStr(d.datum),
          stillstand: !!d.stillstand, foto_doku: !!d.foto_doku, anfahrt: !!d.anfahrt,
          sig_ersteller: d.sig_ersteller || '', sig_bauleiter: d.sig_bauleiter || '',
          // M2.2: Auto-Zeilen sind server-generiert und werden beim Speichern
          // aus dem Rezept neu abgeleitet - sie duerfen im Formular weder
          // editierbar erscheinen noch mitgesendet werden.
          material: (d.material || []).filter(m => !m.auto_generiert).map(m => ({ ...m })),
          stunden: stundenRows,
        });
      }).catch(() => {});
    } else {
      setForm({ ...EMPTY(), bauleiter: '' });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setMat = (i, k, v) => setForm(f => { const m = [...f.material]; m[i] = { ...m[i], [k]: v }; return { ...f, material: m }; });

  // ── M2.2: Live-Vorschau der automatischen Lager-Abzuege ────────────────────
  // Client-Kopie der kaufmaennischen Rundung aus server.js (kaufmRound, 2
  // Nachkommastellen) - die Vorschau muss dieselben Zahlen zeigen, die der
  // Server beim Speichern bucht.
  const rund2 = (n) => Math.round(Math.round(n * 1000) / 10) / 100;
  const fmtM = (n) => Number(n).toLocaleString('de-DE', { maximumFractionDigits: 2 });
  // Faktor mit 3 Nachkommastellen anzeigen - fmtM (2 NK) wuerde 0,125 als
  // 0,13 zeigen und '8 x 0,13 = 1' waere irrefuehrend (Rechnung nutzt den
  // exakten Faktor, nur die Anzeige war gerundet).
  const fmtFaktorDe = (n) => Number(n).toLocaleString('de-DE', { maximumFractionDigits: 3 });

  // Rezept-Expansion fuer eine Formularzeile (nur Positions-Zeilen mit Menge).
  const autoVorschauFuer = (m) => {
    if (!m.projekt_position_id) return [];
    const menge = Number(m.menge);
    if (!Number.isFinite(menge) || menge <= 0) return [];
    return rezepte
      .filter(r => r.projekt_position_id === Number(m.projekt_position_id))
      .map(r => ({ ...r, autoMenge: rund2(menge * Number(r.faktor)) }));
  };

  // Gesamt-Abzug je Material ueber ALLE Zeilen (mehrere Positionen koennen
  // dasselbe Material ziehen; manuelle Lager-Zeilen zaehlen dazu) - Basis
  // fuer Unterdeckungs- und Mehrverbrauchs-Hinweise.
  const autoSummen = (() => {
    const map = new Map();
    for (const m of form.material) {
      for (const v of autoVorschauFuer(m)) {
        if (v.material_aktiv === false || v.autoMenge <= 0) continue;
        const e = map.get(v.material_id)
          || { material_id: v.material_id, bezeichnung: v.bezeichnung, einheit: v.einheit, bestand: Number(v.bestand), auto: 0, manuell: 0 };
        e.auto = rund2(e.auto + v.autoMenge);
        map.set(v.material_id, e);
      }
    }
    for (const m of form.material) {
      const mid = Number(m.material_id);
      if (mid && map.has(mid) && Number(m.menge) > 0) {
        map.get(mid).manuell = rund2(map.get(mid).manuell + Number(m.menge));
      }
    }
    return [...map.values()].map(e => {
      const gesamt = rund2(e.auto + e.manuell);
      return { ...e, gesamt, fehlt: Number.isFinite(e.bestand) && e.bestand - gesamt < 0 ? rund2(gesamt - e.bestand) : 0 };
    });
  })();
  const summeFuer = (materialId) => autoSummen.find(s => s.material_id === materialId);

  // Eine Vorschau-Sub-Zeile unter einer Positions-Zeile (Desktop + Mobile).
  const renderVorschau = (m) => {
    const vorschau = autoVorschauFuer(m);
    if (vorschau.length === 0) return null;
    return (
      <div style={{ fontSize: 11, lineHeight: 1.6, color: 'var(--text-3)' }}>
        {vorschau.map((v, k) => {
          if (v.material_aktiv === false) {
            return <div key={k} style={{ color: 'var(--amber, #d49a3a)' }}>⚙ {v.bezeichnung}: Material archiviert — wird <strong>nicht</strong> abgezogen</div>;
          }
          if (v.autoMenge <= 0) {
            return <div key={k} style={{ color: 'var(--amber, #d49a3a)' }}>⚙ {v.bezeichnung}: Menge rundet auf 0 — kein Abzug</div>;
          }
          const s = summeFuer(v.material_id);
          return (
            <div key={k}>
              ⚙ automatisch: {fmtM(m.menge)} × {fmtFaktorDe(v.faktor)} = <strong>{fmtM(v.autoMenge)} {v.einheit || ''}</strong> {v.bezeichnung}
              {s && s.fehlt > 0 && <span style={{ color: 'var(--red, #d9534f)' }}> — Bestand reicht nicht (fehlen {fmtM(s.fehlt)} {v.einheit || ''})</span>}
              {s && s.manuell > 0 && <span style={{ color: 'var(--amber, #d49a3a)' }}> · wird zusätzlich manuell gebucht (Mehrverbrauch)</span>}
            </div>
          );
        })}
      </div>
    );
  };

  // A.5.3: calcHours wurde entfernt. Std. gesamt / Monteure / Gesamt MA
  // sind jetzt read-only Stat-Boxen, deren Werte aus calcAggregat(form.stunden)
  // live berechnet werden (Normalfall) bzw. aus den DB-Header-Feldern
  // uebernommen (bei Migrations-Sammelzeile). Arbeitsbeginn/-ende bleiben
  // editierbar als Tages-Zeitfenster, triggern aber keine Aggregat-Berechnung
  // mehr.

  const loadWetter = () => {
    if (!navigator.geolocation) return;
    setWetterLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&current=temperature_2m,weathercode,windspeed_10m,precipitation&timezone=auto`);
        const d = await r.json();
        const codes = { 0:'Klar',1:'Meist klar',2:'Teilweise bewölkt',3:'Bedeckt',45:'Nebel',48:'Nebel',51:'Nieselregen',61:'Leichter Regen',63:'Regen',65:'Starkregen',71:'Leichter Schnee',73:'Schnee',80:'Schauer',81:'Schauer',95:'Gewitter' };
        setForm(f => ({ ...f, temperatur: `${Math.round(d.current.temperature_2m)}°C`, wetter: codes[d.current.weathercode] || 'Unbekannt', wind: `${Math.round(d.current.windspeed_10m)} km/h`, niederschlag: d.current.precipitation > 0 ? `${d.current.precipitation} mm` : 'Kein Niederschlag' }));
      } catch {}
      setWetterLoading(false);
    }, () => setWetterLoading(false));
  };

  // A.5.2 Pre-Save-Validation der Stundenzeilen. Spart Roundtrip ans
  // Backend (das die gleichen Regeln nochmal prueft) und liefert sofortige
  // UX-Rueckmeldung. Backend ist die Source of Truth.
  const validateStunden = () => {
    for (let i = 0; i < form.stunden.length; i++) {
      const s = form.stunden[i];
      const nr = i + 1;
      if (!s.rolle_id) return `Stundenzeile ${nr}: Funktion fehlt`;
      const std = Number(s.stunden);
      if (!Number.isFinite(std) || std <= 0) {
        return `Stundenzeile ${nr}: Stunden muss eine Zahl > 0 sein`;
      }
      // A.7 weiche Pflicht (User-Entscheidung 20.07.2026): externe Zeile
      // braucht Subunternehmer ODER Bemerkung. Mit gewaehltem Sub ist die
      // Bemerkung optional; Legacy-/Freitext-Fallback bleibt erhalten.
      if (s.user_id == null && s.subunternehmer_id == null && (!s.bemerkung || !s.bemerkung.trim())) {
        return `Stundenzeile ${nr}: Subunternehmer wählen oder Bemerkung ausfüllen (extern)`;
      }
    }
    return null;
  };

  const save = async (status) => {
    setSaving(true); setMsg('');
    // Validate vor dem POST/PUT - SaveStundenError aus Backend wuerde sonst
    // auch greifen, aber UX ist mit fruehem Hint deutlich besser.
    const stundenErr = validateStunden();
    if (stundenErr) { setMsg(stundenErr); setSaving(false); return; }
    try {
      // Stunden in Backend-Payload-Form bringen: _key entfernen, Position
      // nur senden wenn das Feld sichtbar war (typ=regie/zusatz oder
      // abrechenbar=true), sonst null. Bemerkung leer -> null.
      const stundenPayload = form.stunden.map(s => ({
        user_id: s.user_id,
        // A.7: nur bei externen Zeilen relevant; Server lehnt die Kombination
        // user_id + subunternehmer_id ohnehin ab (400).
        subunternehmer_id: s.user_id == null ? (s.subunternehmer_id || null) : null,
        rolle_id: parseInt(s.rolle_id),
        stunden: Number(s.stunden),
        typ: s.typ,
        abrechenbar: !!s.abrechenbar,
        projekt_position_id: positionFieldVisible(s) ? (s.projekt_position_id || null) : null,
        bemerkung: (s.bemerkung && s.bemerkung.trim()) ? s.bemerkung : null,
      }));
      const payload = { ...form, status, stunden: stundenPayload };
      // A.5.3: Aggregat-Felder im Header conditional setzen.
      // Bei Migrations-Sammelzeile bleiben die Original-DB-Werte aus form
      // (spread oben) - Backend-Header-UPDATE schreibt sie unveraendert,
      // A.3-Recalc-Schutz laesst anzahl_monteure/std_gesamt stehen.
      // Sonst: live-Aggregate aus calcAggregat ueberschreiben die alten
      // Werte, Backend-Recalc setzt sie nach saveStunden ohnehin nochmal
      // (idempotent, Source of Truth = bautagebuch_stunden).
      const aggSave = calcAggregat(form.stunden);
      const isMigrationsBT = form.stunden.length === 1
        && isMigrationsZeile(form.stunden[0]);
      if (!isMigrationsBT) {
        payload.std_gesamt = aggSave.durchschnitt != null
          ? aggSave.durchschnitt.toFixed(2) : null;
        payload.anzahl_monteure = aggSave.personen > 0 ? aggSave.personen : null;
        payload.gesamtstunden = aggSave.summe > 0
          ? Number(aggSave.summe.toFixed(2)) : null;
      }
      let savedId;
      let resp;
      if (isEdit && id) {
        resp = await api.put(`/bautagebuecher/${id}`, payload);
        savedId = id;
      } else {
        resp = await api.post('/bautagebuecher', payload);
        savedId = resp.data.id;
      }
      // M2.2: Server-Warnungen aus der Rezept-Expansion (Unterdeckung,
      // archiviertes Material) anzeigen - gespeichert ist trotzdem.
      const matWarn = resp.data.material_warnungen || [];
      if (matWarn.length > 0) {
        window.alert('Gespeichert — Hinweise zum Material:\n\n• ' + matWarn.join('\n• '));
      }
      setMsg('Gespeichert ✓');
      setTimeout(() => navigate(`/bautagebuch/${savedId}`), 600);
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Fehler beim Speichern');
    }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{isEdit ? 'Bautagebuch bearbeiten' : 'Neues Bautagebuch'}</div>
          <div className="page-sub">{isEdit ? `#${id}` : 'Neuer Eintrag'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>Abbrechen</button>
          <button className="btn btn-secondary" onClick={() => save('entwurf')} disabled={saving}>Entwurf</button>
          <button className="btn btn-primary" onClick={() => save('fertig')} disabled={saving}>{saving ? 'Speichert…' : 'Abschließen'}</button>
        </div>
      </div>

      {msg && <div className={`alert ${msg.includes('Fehler') ? 'alert-error' : 'alert-success'}`}>{msg}</div>}

      <div className="card card-accent">
        <div className="form-section">Kopfdaten</div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          <div className="form-group"><label className="form-label">Datum</label><input className="form-input" type="date" value={form.datum} onChange={e => set('datum', e.target.value)} /></div>
          <div className="form-group">
            <label className="form-label">Baustelle</label>
            <select className="form-select" value={form.baustelle_id} onChange={e => set('baustelle_id', e.target.value)}>
              <option value="">— wählen —</option>
              {baustellen.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Bauleiter / Polier</label><input className="form-input" value={form.bauleiter} onChange={e => set('bauleiter', e.target.value)} /></div>
        </div>

        <div className="form-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
          <span>Witterungsbedingungen</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadWetter} disabled={wetterLoading} style={{ marginBottom: 6 }}>
            {wetterLoading ? '⏳ Lädt…' : '🌤 Auto'}
          </button>
        </div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          {[['Temperatur', 'temperatur'], ['Wetter', 'wetter'], ['Wind', 'wind'], ['Niederschlag', 'niederschlag'], ['Sichtweite', 'sichtweite'], ['Bemerkung', 'wetter_bem']].map(([l, k]) => (
            <div className="form-group" key={k}><label className="form-label">{l}</label><input className="form-input" value={form[k]} onChange={e => set(k, e.target.value)} /></div>
          ))}
        </div>

        <div className="form-section" style={{ marginTop: 18 }}>Personal & Arbeitszeit</div>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Beginn</label>
            <input
              className="form-input" type="time"
              value={form.arbeitsbeginn}
              onChange={e => set('arbeitsbeginn', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Ende</label>
            <input
              className="form-input" type="time"
              value={form.arbeitsende}
              onChange={e => set('arbeitsende', e.target.value)}
            />
          </div>
        </div>

        {/* A.5.3: Read-only Aggregat-Anzeige. Bei Migrations-Sammelzeile
            werden die geladenen DB-Werte gezeigt (anzahl_monteure / gesamtstunden
            / std_gesamt aus form), sonst die live-berechneten Werte aus
            calcAggregat(form.stunden). Wechsel ist automatisch, sobald die
            Sammelzeile aufgesplittet oder geloescht wird. */}
        {(() => {
          const aggView = calcAggregat(form.stunden);
          const isMigrationsBT = form.stunden.length === 1
            && isMigrationsZeile(form.stunden[0]);
          const toNumOrNull = (v) => {
            if (v == null || v === '') return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
          };
          const personen = isMigrationsBT
            ? toNumOrNull(form.anzahl_monteure)
            : aggView.personen;
          const gesamt = isMigrationsBT
            ? toNumOrNull(form.gesamtstunden)
            : aggView.summe;
          const stdPro = isMigrationsBT
            ? toNumOrNull(form.std_gesamt)
            : aggView.durchschnitt;
          const fmtNum = (v) => (v == null || !Number.isFinite(v)) ? '–' : Number(v).toFixed(2);
          const fmtInt = (v) => (v == null || !Number.isFinite(v)) ? '–' : String(Math.round(v));
          return (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 10,
              }}>
                <div style={{ background: 'var(--bg-elevated)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Personen</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{fmtInt(personen)}</div>
                </div>
                <div style={{ background: 'var(--bg-elevated)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Gesamt-Mannstunden</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--copper)' }}>
                    {gesamt != null && Number.isFinite(gesamt) ? `${fmtNum(gesamt)} h` : '–'}
                  </div>
                </div>
                <div style={{ background: 'var(--bg-elevated)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Ø Stunden / Person</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {stdPro != null && Number.isFinite(stdPro) ? `${fmtNum(stdPro)} h` : '–'}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: 11,
                color: 'var(--text-3)',
                marginTop: 6,
                fontStyle: isMigrationsBT ? 'italic' : 'normal',
              }}>
                {isMigrationsBT
                  ? 'Werte aus alter Sammelerfassung übernommen.'
                  : 'Werte werden automatisch aus den Stundenzeilen unten berechnet.'}
              </div>
            </>
          );
        })()}
      </div>

      {/* A.5.2: Stunden pro Mitarbeiter. Alte Header-Felder werden in A.5.3
          read-only und automatisch aus dieser Liste aggregiert. */}
      <StundenSection
        form={form}
        setForm={setForm}
        users={users}
        rollen={rollen}
        subunternehmer={subunternehmer}
        projektPositionen={projektPositionen}
        baustelleId={form.baustelle_id}
      />

      <div className="card">
        <div className="card-head" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="card-title">Materialeinlage</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Standard-Lagerkatalog */}
            <select className="form-select" style={{ width: 'auto', maxWidth: 280 }} value="" onChange={e => {
              const p = katalog.find(x => x.id === parseInt(e.target.value));
              if (!p || form.material.find(m => m.material_id === p.id)) return;
              setForm(f => ({ ...f, material: [...f.material, { material_id: p.id, projekt_position_id: null, pos_nr: p.pos_nr || '', bezeichnung: p.bezeichnung, einheit: p.einheit, menge: '', lage: '', bemerkung: '' }] }));
            }}>
              <option value="">+ Standardposition hinzufügen</option>
              {katalog.filter(p => !form.material.find(m => m.material_id === p.id)).map(p => (
                <option key={p.id} value={p.id}>{p.pos_nr ? p.pos_nr + ' – ' : ''}{p.bezeichnung} ({p.bestand} {p.einheit})</option>
              ))}
            </select>
            {/* Projektpositionen der gewählten Baustelle */}
            <select
              className="form-select"
              style={{ width: 'auto', maxWidth: 280 }}
              value=""
              disabled={!form.baustelle_id}
              title={!form.baustelle_id ? 'Erst Baustelle wählen' : ''}
              onChange={e => {
                const p = projektPositionen.find(x => x.id === parseInt(e.target.value));
                if (!p || form.material.find(m => m.projekt_position_id === p.id)) return;
                setForm(f => ({ ...f, material: [...f.material, {
                  material_id: null,
                  projekt_position_id: p.id,
                  pos_nr: p.pos_nr || '',
                  bezeichnung: p.bezeichnung,
                  einheit: p.einheit || '',
                  menge: '', lage: '', bemerkung: '',
                }]}));
              }}
            >
              <option value="">{form.baustelle_id ? '+ Projektposition hinzufügen' : '+ Projektposition (erst Baustelle wählen)'}</option>
              {projektPositionen
                .filter(p => !form.material.find(m => m.projekt_position_id === p.id))
                .map(p => (
                  <option key={p.id} value={p.id}>
                    {p.pos_nr ? p.pos_nr + ' – ' : ''}{p.bezeichnung}
                    {p.sollmenge != null && p.einheit ? ` (Soll ${p.sollmenge} ${p.einheit})` : ''}
                    {p.nachtrag ? ' [Nachtrag]' : ''}
                  </option>
                ))}
            </select>
          </div>
        </div>
        {form.material.length === 0 ? (
          <div className="empty"><div className="empty-icon">📦</div><h3>Kein Material</h3><p>Oben Positionen hinzufügen</p></div>
        ) : (
          <>
            {/* Desktop: kompakte Tabelle */}
            <div className="table-wrap desktop-only">
              <table>
                <thead><tr><th>Pos.-Nr.</th><th>Bezeichnung</th><th>Einh.</th><th style={{ width: 80 }}>Menge</th><th>Lage</th><th>Bemerkung</th><th></th></tr></thead>
                <tbody>
                  {form.material.map((m, i) => {
                    const vorschau = renderVorschau(m);
                    return (
                    <Fragment key={i}>
                    <tr>
                      <td className="text-mono text-copper" style={{ fontWeight: 600 }}>{m.pos_nr}</td>
                      <td>
                        {m.bezeichnung}
                        {m.projekt_position_id && <span className="badge badge-copper" style={{ marginLeft: 8, fontSize: 9 }}>Projekt</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>{m.einheit}</td>
                      <td><input className="form-input" value={m.menge} onChange={e => setMat(i, 'menge', e.target.value)} style={{ padding: '5px 8px' }} inputMode="decimal" /></td>
                      <td><input className="form-input" value={m.lage} onChange={e => setMat(i, 'lage', e.target.value)} style={{ padding: '5px 8px' }} /></td>
                      <td><input className="form-input" value={m.bemerkung} onChange={e => setMat(i, 'bemerkung', e.target.value)} style={{ padding: '5px 8px' }} /></td>
                      <td><button type="button" onClick={() => setForm(f => ({ ...f, material: f.material.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 18 }}>×</button></td>
                    </tr>
                    {vorschau && (
                      <tr>
                        <td style={{ borderBottom: 'none' }}></td>
                        <td colSpan={6} style={{ paddingTop: 0 }}>{vorschau}</td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile: gestapelte Karten — verhindert Overflow rechts */}
            <div className="mat-cards mobile-only">
              {form.material.map((m, i) => (
                <div key={i} className="mat-card">
                  <div className="mat-card-head">
                    <div className="mat-card-headline">
                      {m.pos_nr && <div className="mat-card-pos">{m.pos_nr}</div>}
                      <div className="mat-card-name">
                        {m.bezeichnung}
                        {m.projekt_position_id && <span className="badge badge-copper" style={{ marginLeft: 8, fontSize: 9 }}>Projekt</span>}
                      </div>
                      {m.einheit && <div className="mat-card-unit">Einheit: {m.einheit}</div>}
                    </div>
                    <button type="button" className="mat-card-del" aria-label="Position entfernen"
                      onClick={() => setForm(f => ({ ...f, material: f.material.filter((_, j) => j !== i) }))}>×</button>
                  </div>
                  <div className="mat-card-grid">
                    <div className="form-group">
                      <label className="form-label">Menge</label>
                      <input className="form-input" value={m.menge} onChange={e => setMat(i, 'menge', e.target.value)} inputMode="decimal" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Lage</label>
                      <input className="form-input" value={m.lage} onChange={e => setMat(i, 'lage', e.target.value)} />
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Bemerkung</label>
                      <input className="form-input" value={m.bemerkung} onChange={e => setMat(i, 'bemerkung', e.target.value)} />
                    </div>
                  </div>
                  {renderVorschau(m) && (
                    <div style={{ marginTop: 8, background: 'var(--bg-elevated)', padding: '6px 10px', borderRadius: 'var(--radius)' }}>
                      {renderVorschau(m)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* M2.2: Summen-Vorschau der automatischen Lager-Abzuege ueber
                alle Zeilen (Server bucht beim Speichern identisch). */}
            {autoSummen.length > 0 && (
              <div style={{
                marginTop: 12, background: 'var(--bg-elevated)', padding: '10px 12px',
                borderRadius: 'var(--radius)', fontSize: 12, lineHeight: 1.7, color: 'var(--text-2)',
              }}>
                <strong>⚙ Automatische Lager-Abzüge (Vorschau):</strong>
                {autoSummen.map((s) => (
                  <div key={s.material_id}>
                    {fmtM(s.auto)} {s.einheit || ''} {s.bezeichnung}
                    {Number.isFinite(s.bestand) && <span style={{ color: 'var(--text-3)' }}> · Bestand {fmtM(s.bestand)}</span>}
                    {s.manuell > 0 && <span style={{ color: 'var(--amber, #d49a3a)' }}> · +{fmtM(s.manuell)} manuell (Mehrverbrauch)</span>}
                    {s.fehlt > 0 && <span style={{ color: 'var(--red, #d9534f)' }}> · reicht nicht — fehlen {fmtM(s.fehlt)}</span>}
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  Wird beim Speichern automatisch gebucht — Unterdeckung blockiert nicht, der Bestand kann negativ werden.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div className="form-section">Leistungen des Tages</div>
        <textarea className="form-textarea" style={{ minHeight: 100 }} value={form.leistungen} onChange={e => set('leistungen', e.target.value)} placeholder="Was wurde heute geleistet…" />

        <div className="form-section" style={{ marginTop: 18 }}>Sonstiges</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
          {[['stillstand', 'Stillstand'], ['foto_doku', 'Fotodokumentation erstellt'], ['anfahrt', 'Anfahrtspauschale (50 €)']].map(([k, l]) => (
            <label key={k} className="check-group" style={{ background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 'var(--radius)' }}>
              <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} /><span>{l}</span>
            </label>
          ))}
        </div>
        {form.stillstand && <div className="form-group"><label className="form-label">Grund für Stillstand</label><input className="form-input" value={form.stillstand_grund} onChange={e => set('stillstand_grund', e.target.value)} /></div>}
      </div>

      <div className="card">
        <div className="form-section">Unterschriften</div>
        <div className="grid-2">
          <div>
            <SignaturePad label="DV Elektro (Ausführender)" value={form.sig_ersteller} onChange={v => set('sig_ersteller', v)} />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{new Date().toLocaleDateString('de-DE')} — {user?.name}</div>
          </div>
          <div>
            <SignaturePad label="Bauleiter / Polier (Auftraggeber)" value={form.sig_bauleiter} onChange={v => set('sig_bauleiter', v)} />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{form.bauleiter || 'Bauleiter'}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/bautagebuch')}>Abbrechen</button>
        <button className="btn btn-secondary" onClick={() => save('entwurf')} disabled={saving}>Entwurf speichern</button>
        <button className="btn btn-primary" onClick={() => save('fertig')} disabled={saving}>{saving ? 'Speichert…' : 'Abschließen & speichern'}</button>
      </div>
    </div>
  );
}

// ─── A.5.2 Stunden-Section: Pro-Person-Zeilen im Bautagebuch-Formular ──
function StundenSection({ form, setForm, users, rollen, subunternehmer, projektPositionen, baustelleId }) {
  // User-Dropdown: alle aktiven plus inaktive, die in den aktuellen Stunden
  // bereits referenziert sind (Bestandsschutz im Edit-Modus). Aktive zuerst.
  const usedUserIds = new Set(form.stunden.map(s => s.user_id).filter(v => v != null));
  const userOptions = users
    .filter(u => u.aktiv || usedUserIds.has(u.id))
    .sort((a, b) => {
      if (a.aktiv !== b.aktiv) return a.aktiv ? -1 : 1;
      return (a.name || a.username || '').localeCompare(b.name || b.username || '');
    });

  // Rollen-Dropdown: aktive plus referenzierte inaktive, sortiert nach reihenfolge.
  const usedRolleIds = new Set(form.stunden.map(s => s.rolle_id).filter(v => v != null));
  const rollenOptions = rollen
    .filter(r => r.aktiv || usedRolleIds.has(r.id))
    .sort((a, b) => (a.reihenfolge || 0) - (b.reihenfolge || 0));

  // A.7 Sub-Dropdown: Optionen-Route liefert nur aktive - referenzierte,
  // inzwischen archivierte Subs defensiv aus den geladenen Zeilen ergaenzen
  // (Bestandsschutz im Edit-Modus, Muster userOptions oben).
  const subOptions = [...subunternehmer];
  for (const s of form.stunden) {
    if (s.subunternehmer_id != null && !subOptions.some(o => o.id === s.subunternehmer_id)) {
      subOptions.push({
        id: s.subunternehmer_id,
        firma: `${s.subunternehmer_firma || `#${s.subunternehmer_id}`} (archiviert)`,
      });
    }
  }

  const addRow = () => setForm(f => ({ ...f, stunden: [...f.stunden, newStundeRow()] }));
  const removeRow = (key) => setForm(f => ({ ...f, stunden: f.stunden.filter(s => s._key !== key) }));

  const updateRow = (key, updates) => {
    setForm(f => ({
      ...f,
      stunden: f.stunden.map(s => {
        if (s._key !== key) return s;
        const next = { ...s, ...updates };
        // Mitarbeiter-Wechsel: Funktion aus User-Default vorbelegen
        // (User kann sie danach manuell weiterhin ueberschreiben).
        if ('user_id' in updates && updates.user_id !== s.user_id) {
          const u = users.find(x => x.id === updates.user_id);
          if (u && u.rolle_id) next.rolle_id = u.rolle_id;
          // A.7: echter Mitarbeiter gewaehlt -> Sub-Zuordnung verwerfen
          // (Server lehnt die Kombination ab).
          if (updates.user_id != null) {
            next.subunternehmer_id = null;
            next.subunternehmer_firma = null;
          }
        }
        // Typ-Wechsel: abrechenbar springt auf typ-Default (regulaer=false,
        // regie/zusatz=true). Touched-State kommt ggf. spaeter, falls UX nervt.
        if ('typ' in updates && updates.typ !== s.typ) {
          next.abrechenbar = typAbrechenbarDefault(updates.typ);
        }
        return next;
      }),
    }));
  };

  const showMigrationsBanner = form.stunden.length === 1 && isMigrationsZeile(form.stunden[0]);

  const agg = calcAggregat(form.stunden);
  const sigmaText = (
    <>
      {agg.personen} {agg.personen === 1 ? 'Person' : 'Personen'}
      {' · '}Gesamt: {agg.summe.toFixed(2)} h
      {agg.durchschnitt != null && agg.personen > 1 && (
        <> {' · '}Ø {agg.durchschnitt.toFixed(2)} h/Person</>
      )}
    </>
  );

  return (
    <div className="card">
      <div className="card-head" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="card-title">Stunden pro Mitarbeiter</div>
          <div className="card-sub" style={{ fontSize: 12 }}>{sigmaText}</div>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
          + Mitarbeiter hinzufügen
        </button>
      </div>

      {showMigrationsBanner && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.10)',
          border: '1px solid rgba(245, 158, 11, 0.35)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          margin: '0 0 12px',
          fontSize: 13,
          lineHeight: 1.4,
        }}>
          ⚠ Diese Stunden wurden aus der alten Sammelerfassung übernommen.
          Bitte pro Person aufschlüsseln und die migrierte Zeile danach löschen.
        </div>
      )}

      {form.stunden.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">👷</div>
          <h3>Keine Stundenzeilen</h3>
          <p>„+ Mitarbeiter hinzufügen" anklicken, um die Tagesstunden pro Person zu erfassen.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {form.stunden.map((row, idx) => (
            <StundeRow
              key={row._key}
              row={row}
              idx={idx}
              userOptions={userOptions}
              rollenOptions={rollenOptions}
              subOptions={subOptions}
              projektPositionen={projektPositionen}
              baustelleId={baustelleId}
              onChange={(updates) => updateRow(row._key, updates)}
              onRemove={() => removeRow(row._key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StundeRow({ row, idx, userOptions, rollenOptions, subOptions, projektPositionen, baustelleId, onChange, onRemove }) {
  const isSubunternehmer = row.user_id == null;
  const showPosition = positionFieldVisible(row);
  // A.7 weiche Pflicht: Bemerkung ist die Pflicht-Alternative, wenn extern
  // UND kein Subunternehmer aus den Stammdaten gewaehlt ist (Freitext-
  // Fallback). Required (Stern) und Invalid (roter Rahmen) getrennt: der
  // Stern bleibt auch sichtbar, wenn die Bemerkung bereits ausgefuellt ist.
  const hatSub = row.subunternehmer_id != null;
  const bemerkungRequired = isSubunternehmer && !hatSub;
  const bemerkungInvalid = bemerkungRequired && (!row.bemerkung || !row.bemerkung.trim());

  const userValue = row.user_id == null ? '' : String(row.user_id);
  const rolleValue = row.rolle_id == null ? '' : String(row.rolle_id);

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      borderRadius: 'var(--radius)',
      padding: 12,
      position: 'relative',
    }}>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Stundenzeile entfernen"
        style={{
          position: 'absolute', top: 6, right: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--red)', fontSize: 20, lineHeight: 1, padding: 4,
        }}
      >×</button>

      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6, paddingRight: 28 }}>
        Zeile {idx + 1}
      </div>

      <div className="form-grid form-grid-2" style={{ marginBottom: 8 }}>
        <div className="form-group">
          <label className="form-label">Mitarbeiter</label>
          <select
            className="form-select"
            value={userValue}
            onChange={e => onChange({ user_id: e.target.value === '' ? null : parseInt(e.target.value) })}
          >
            <option value="">— Subunternehmer/extern —</option>
            {userOptions.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.username}{u.aktiv ? '' : ' (inaktiv)'}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Funktion</label>
          <select
            className="form-select"
            value={rolleValue}
            onChange={e => onChange({ rolle_id: e.target.value === '' ? null : parseInt(e.target.value) })}
          >
            <option value="">— wählen —</option>
            {rollenOptions.map(r => (
              <option key={r.id} value={r.id}>
                {r.bezeichnung}{r.aktiv ? '' : ' (inaktiv)'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-grid form-grid-2" style={{ marginBottom: 8 }}>
        <div className="form-group">
          <label className="form-label">Stunden</label>
          <input
            className="form-input"
            type="number" step="0.25" min="0.25" inputMode="decimal"
            value={row.stunden}
            onChange={e => onChange({ stunden: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Typ</label>
          <select
            className="form-select"
            value={row.typ}
            onChange={e => onChange({ typ: e.target.value })}
          >
            {STUNDEN_TYPEN.map(t => (
              <option key={t} value={t}>{TYP_LABELS[t]}</option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="form-grid form-grid-2"
        style={{ marginBottom: 8, alignItems: 'end' }}
      >
        <label
          className="check-group"
          style={{
            background: 'var(--bg-card)', padding: '8px 12px',
            borderRadius: 'var(--radius)', cursor: 'pointer',
            display: 'inline-flex', gap: 8, alignItems: 'center',
            alignSelf: 'start',
          }}
        >
          <input
            type="checkbox"
            checked={!!row.abrechenbar}
            onChange={e => onChange({ abrechenbar: e.target.checked })}
          />
          <span style={{ fontSize: 13 }}>abrechenbar</span>
        </label>
        {showPosition && (
          <div className="form-group">
            <label className="form-label">Position (optional)</label>
            <select
              className="form-select"
              value={row.projekt_position_id || ''}
              disabled={!baustelleId}
              onChange={e => onChange({ projekt_position_id: e.target.value === '' ? null : parseInt(e.target.value) })}
            >
              <option value="">— keine —</option>
              {projektPositionen.map(p => (
                <option key={p.id} value={p.id}>
                  {p.pos_nr ? p.pos_nr + ' – ' : ''}{p.bezeichnung}
                  {p.nachtrag ? ' [Nachtrag]' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isSubunternehmer && (
        <div className="form-group" style={{ marginBottom: 8 }}>
          <label className="form-label">Subunternehmer (Stammdaten)</label>
          <select
            className="form-select"
            value={row.subunternehmer_id == null ? '' : String(row.subunternehmer_id)}
            onChange={e => onChange({ subunternehmer_id: e.target.value === '' ? null : parseInt(e.target.value) })}
          >
            <option value="">— nicht in Stammdaten (Freitext unten) —</option>
            {subOptions.map(s => (
              <option key={s.id} value={s.id}>{s.firma}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label">
          Bemerkung{bemerkungRequired && <span style={{ color: 'var(--red)' }}> *</span>}
        </label>
        <input
          className="form-input"
          value={row.bemerkung}
          onChange={e => onChange({ bemerkung: e.target.value })}
          placeholder={
            isSubunternehmer
              ? (hatSub
                  ? 'optional, z. B. Kolonne/Details'
                  : 'Firma/Name des Externen (Pflicht ohne Stammdaten-Auswahl)')
              : 'optional'
          }
          style={bemerkungInvalid ? { borderColor: 'var(--red)' } : undefined}
        />
      </div>
    </div>
  );
}
